import type { ReactElement } from "react";
import cx from "classnames";
import { graphql, useFragment } from "react-relay";
import { Text, MessageBanner, CalendarIcon } from "@olivahealth/oli-ui";
import { useMediaQuery } from "react-responsive";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Mousewheel, Navigation } from "swiper/modules";
import Link from "next/link";
import tokens from "@olivahealth/oli-ui/theme/tokens";
import useTranslation from "../../../hooks/useTranslation";
import useCanAccessProfessionalCoaching from "../../../hooks/useCanAccessProfessionalCoaching";
import SessionCard from "../../organisms/SessionCard";
import { UpcomingEventsFragment_data$key as IFragment } from "./__generated__/UpcomingEventsFragment_data.graphql";
import "swiper/css";

interface Props {
  addSpaceAfter: boolean;
  data: IFragment;
}

const UpcomingEventsFragment = graphql`
  fragment UpcomingEventsFragment_data on Query
  @argumentDefinitions(userId: { type: "String" }) {
    sessions(
      filterBy: { userId: $userId }
      only: UPCOMING
      include: { groupSessions: true, cancelled: false }
      sortBy: { scheduledDate: ASC }
      first: 2
    ) {
      nodes {
        __typename
        ... on GroupSession {
          id
        }
        ... on Session {
          id
        }
        ...useSessionCard_session
      }
    }
  }
`;

export default function UpcomingEvents({
  addSpaceAfter,
  data,
}: Props): ReactElement {
  const { t } = useTranslation("employeeDashboard", {
    keyPrefix: "upcomingEvents",
  });
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  const fragmentData = useFragment<IFragment>(UpcomingEventsFragment, data);
  const sessions =
    fragmentData?.sessions.nodes.filter(
      (session) => Object.keys(session).length !== 0,
    ) ?? [];

  const hasEmptyState = !sessions.length;

  return (
    <div>
      <Text weight="bold" size="xl" gutter="lg" color="white">
        {t("title")}
      </Text>

      {hasEmptyState && <EmptyState />}

      <div
        className={cx("flex flex-col", {
          "md:mb-14": addSpaceAfter,
        })}
        data-testid="upcoming-events"
      >
        {!hasEmptyState && isTabletOrMobile && (
          <div>
            <Swiper
              grabCursor
              modules={[A11y, Mousewheel, Navigation]}
              mousewheel={{
                releaseOnEdges: true,
              }}
              spaceBetween={16}
              slidesPerView={sessions.length === 1 ? 1 : 1.5}
              threshold={20}
              className="!p-1"
            >
              {sessions.map((session) => (
                <SwiperSlide key={session.id}>
                  <SessionCard session={session} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
        {!hasEmptyState && !isTabletOrMobile && (
          <div className="grid grid-cols-2 gap-3">
            {sessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  const { t, Trans } = useTranslation("employeeDashboard", {
    keyPrefix: "upcomingEvents",
  });
  const canAccessCoaching = useCanAccessProfessionalCoaching();

  const baseKey = canAccessCoaching
    ? "emptyStateWithCoaching"
    : "emptyStateNoCoaching";
  const pageCount = canAccessCoaching ? 2 : 1;
  const components = canAccessCoaching
    ? [
        <Link key="supportLink" href="/support" className="underline" />,
        <Link
          key="coachingLink"
          href="/professional-coaching"
          className="underline"
        />,
      ]
    : [<Link key="supportLink" href="/support" className="underline" />];

  const description = (
    <span className="text-neutral-200">
      <Trans
        i18nKey={t(baseKey, { count: pageCount })}
        components={components}
      />
    </span>
  );

  return (
    <MessageBanner
      variant="info"
      message={t("emptyStateTitle")}
      description={description}
      messageIcon={<CalendarIcon color={tokens.colors["status-info"][300]} />}
      isOpen={true}
      showCloseButton={false}
    />
  );
}
